import React from 'react';
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  alpha,
  useTheme,
  keyframes,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  AutoAwesome,
  EventAvailable,
  PeopleAltOutlined,
  TuneOutlined,
  RestartAlt,
} from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
  onRelaunch?: () => void;
}

// Subtle scale-in for the success badge so the modal feels alive without
// being garish. Animations stay short (350ms) and respect prefers-reduced-motion
// through the @media query inside the keyframes definition.
const popIn = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  60%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); }
`;

const checkPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 var(--pulse-color); }
  100% { box-shadow: 0 0 0 20px transparent; }
`;

const sparkle = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.55; }
  50%      { transform: scale(1.2) rotate(15deg); opacity: 0.9; }
`;

/**
 * Celebratory completion modal shown when a school admin reaches the end of
 * the welcome tour. Visual language matches the rest of the portal (Paper
 * tints, primary gradient accents, MUI Typography hierarchy) — the only
 * "splashy" element is a single animated checkmark badge.
 */
const TourCompletionDialog: React.FC<Props> = ({ open, onClose, onRelaunch }) => {
  const theme = useTheme();
  const pulseColor = alpha(theme.palette.success.main, 0.25);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06)',
          },
        },
      }}
    >
      {/* Hero band — gradient backdrop with the success badge + sparkles */}
      <Box
        sx={{
          position: 'relative',
          py: 4,
          px: 3,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Decorative sparkles in the corners */}
        <AutoAwesome
          sx={{
            position: 'absolute',
            top: 22,
            left: 28,
            fontSize: 18,
            color: alpha(theme.palette.primary.main, 0.55),
            animation: `${sparkle} 3.2s ease-in-out infinite`,
          }}
        />
        <AutoAwesome
          sx={{
            position: 'absolute',
            top: 36,
            right: 36,
            fontSize: 14,
            color: alpha(theme.palette.success.main, 0.6),
            animation: `${sparkle} 2.8s ease-in-out infinite`,
            animationDelay: '0.4s',
          }}
        />
        <AutoAwesome
          sx={{
            position: 'absolute',
            bottom: 18,
            right: 24,
            fontSize: 16,
            color: alpha(theme.palette.primary.main, 0.4),
            animation: `${sparkle} 3.6s ease-in-out infinite`,
            animationDelay: '0.9s',
          }}
        />

        {/* Close button — subtle, top-right */}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.disabled',
            '&:hover': { color: 'text.secondary', bgcolor: alpha(theme.palette.text.primary, 0.04) },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Success badge with pulse */}
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: 'auto',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
            color: theme.palette.success.main,
            animation: `${popIn} 350ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              animation: `${checkPulse} 1.6s ease-out infinite`,
              ['--pulse-color' as any]: pulseColor,
            },
          }}
        >
          <CheckCircle sx={{ fontSize: 44 }} />
        </Box>

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: 'text.primary', mt: 2, lineHeight: 1.2 }}
        >
          You're all set
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, maxWidth: 320, mx: 'auto' }}>
          Tour complete. You know the lay of the land — time to set up your school.
        </Typography>
      </Box>

      {/* Recap pills — what was covered */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, fontSize: '0.65rem' }}
        >
          You explored
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip
            icon={<TuneOutlined sx={{ fontSize: 14 }} />}
            label="Settings"
            size="small"
            sx={{
              height: 24,
              fontSize: '0.72rem',
              fontWeight: 500,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              '& .MuiChip-icon': { color: theme.palette.primary.main, ml: 0.5 },
            }}
          />
          <Chip
            icon={<EventAvailable sx={{ fontSize: 14 }} />}
            label="Academic Sessions"
            size="small"
            sx={{
              height: 24,
              fontSize: '0.72rem',
              fontWeight: 500,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              color: theme.palette.info.main,
              '& .MuiChip-icon': { color: theme.palette.info.main, ml: 0.5 },
            }}
          />
          <Chip
            icon={<PeopleAltOutlined sx={{ fontSize: 14 }} />}
            label="Enrollments"
            size="small"
            sx={{
              height: 24,
              fontSize: '0.72rem',
              fontWeight: 500,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              color: theme.palette.success.main,
              '& .MuiChip-icon': { color: theme.palette.success.main, ml: 0.5 },
            }}
          />
        </Stack>
      </Box>

      {/* Footer — relaunch hint + primary CTA */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        {onRelaunch ? (
          <Button
            size="small"
            startIcon={<RestartAlt sx={{ fontSize: 16 }} />}
            onClick={onRelaunch}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.8125rem',
              '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
            }}
          >
            Replay tour
          </Button>
        ) : (
          <Box />
        )}
        <Button
          variant="contained"
          onClick={onClose}
          disableElevation
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
            '&:hover': { boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}` },
          }}
        >
          Start exploring
        </Button>
      </Box>
    </Dialog>
  );
};

export default TourCompletionDialog;
